import { ClientLogger } from "bernie-client";
import { SystemEvent, SystemEventLevel } from "bernie-logger";
import { FlightHiddenSearchDataProps } from "components/flexComponents/WizardFlightPWA/typings";
import { FlightType } from "stores/wizard/state/typings";
import { TravelersMetadata } from "components/shared/TravelersField/typings";
import { ContextStore, ExperimentStore, PageStore } from "bernie-core";

type QueryParams = {
  mode: string;
  leg1: string;
  leg2?: string;
  "flight-type": string;
  trip: string;
  options: string;
  passengers: string;
};

export type FlightsPredictiveSearchURLParams = {
  flightHiddenSearchDataProps: FlightHiddenSearchDataProps;
  travelersMetadata: TravelersMetadata;
  experiment?: ExperimentStore;
  context?: ContextStore;
  page?: PageStore;
  multiCityFlightLegURL?: string[];
};

export const getFlightsPredictiveSearchURL = (
  flightHiddenSearchDataProps: FlightHiddenSearchDataProps,
  travelersMetadata: TravelersMetadata,
  multiCityFlightLegURL?: string[]
): string => {
  const { flightType, flightAirline, mode, trip, leg1, leg2, flightClass, maxhops } = flightHiddenSearchDataProps;
  const { flightSearch } = travelersMetadata;
  const tripType = trip === FlightType.MULTI_CITY ? "multi" : trip;
  const flightOptions = flightAirline ? `${flightClass},carrier:${flightAirline.value}` : `${flightClass}`;
  const queryParams: QueryParams = {
    mode,
    leg1,
    leg2,
    "flight-type": flightType,
    trip: tripType,
    options: `${flightOptions}${maxhops || ""}`,
    passengers: flightSearch,
  };

  const queryParamsKeys = Object.keys(queryParams);

  let flightsPredictiveSearchURL = queryParamsKeys.reduce((currentURL, currentQueryParamKey, currentIndex) => {
    if (typeof queryParams[currentQueryParamKey] === "string" && queryParams[currentQueryParamKey]) {
      currentURL += `${currentQueryParamKey}=${queryParams[currentQueryParamKey]}`;
      if (currentIndex < queryParamsKeys.length - 1) {
        currentURL += "&";
      }
    }

    return currentURL;
  }, `${window.location.origin}/flights-predictive-search?`);

  if (multiCityFlightLegURL?.length && trip === FlightType.MULTI_CITY) {
    flightsPredictiveSearchURL = multiCityFlightLegURL.reduce(
      (currentURL, currentMultiCityFlightLegURL, currentIndex) => {
        if (typeof currentMultiCityFlightLegURL === "string" && currentMultiCityFlightLegURL) {
          currentURL += `leg${currentIndex + 2}=${currentMultiCityFlightLegURL}`;
          if (currentIndex < multiCityFlightLegURL.length - 1) {
            currentURL += "&";
          }
        }

        return currentURL;
      },
      `${flightsPredictiveSearchURL}&`
    );
  }

  return flightsPredictiveSearchURL;
};

export const FSR_PREDICTIVE_SEARCH_RT = "FSR_PREDICTIVE_SEARCH_RT";

export const FSR_PREDICTIVE_SEARCH_OW = "FSR_PREDICTIVE_SEARCH_OW";

export const FSR_PREDICTIVE_SEARCH_MD = "FSR_PREDICTIVE_SEARCH_MD";

export const getExperimentName = (trip: string): string => {
  switch (trip) {
    case FlightType.ROUND_TRIP:
      return FSR_PREDICTIVE_SEARCH_RT;
    case FlightType.ONE_WAY:
      return FSR_PREDICTIVE_SEARCH_OW;
    case FlightType.MULTI_CITY:
      return FSR_PREDICTIVE_SEARCH_MD;
    default:
      return "";
  }
};

export const triggerFlightsPredictiveSearchURL = async (
  flightsPredictiveSearchURLParams: FlightsPredictiveSearchURLParams
) => {
  const {
    flightHiddenSearchDataProps,
    travelersMetadata,
    experiment,
    page,
    multiCityFlightLegURL,
    context,
  } = flightsPredictiveSearchURLParams;
  const logger = ClientLogger.getLoggerWithIdentifier("processFlightsPredictiveSearch");
  try {
    const experimentName = getExperimentName(flightHiddenSearchDataProps.trip);
    const experimentExposure = experiment?.exposures?.[experimentName];
    const isFSRPredictiveSearchEnabled = experimentExposure?.bucket;
    if (experimentExposure && context && page) {
      experiment?.logExperiment(context, page, experimentExposure);
    }
    if (!isFSRPredictiveSearchEnabled) {
      return;
    }
    const flightsPredictiveSearchURL = getFlightsPredictiveSearchURL(
      flightHiddenSearchDataProps,
      travelersMetadata,
      multiCityFlightLegURL
    );

    if (flightsPredictiveSearchURL) {
      await fetch(flightsPredictiveSearchURL);
    }
  } catch (error) {
    logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, "ENVERROR"), error);
  }
};
